import { Component, Input, OnInit, signal } from '@angular/core';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { FeatureSwitchKeys } from '../../../_constants/feature-switch-keys';
import { isInternalStaff } from '../../../_helpers/can-use/current-user-roles';
import { ThemeService } from '../../../_helpers/theming-engine/theme.service';
import { PlacementData } from '../../../_models';
import { PlacementMaintenance } from '../../../_models/maintenance/placement-maintenance.model';
import { FeatureSwitchService } from '../../../_services/feature-switch/feature-switch.service';

/**
 * The placement context strip shown in the utility bar on every tutor-report family screen
 * (subject/brand/delivery tags, tutor, type, id, dates, group, learners). The outer wrapper is
 * always rendered: the utility bar hides itself when its template produces no element, so the
 * null guard has to sit inside this component rather than around it.
 *
 * Behind programme-overview.orbit-order, internal staff see the booking's Orbit order number in place
 * of Group, linked to the order in Orbit TMS. Tutors and client users have no Orbit access and never see it.
 */
@Component({
    selector: 'placement-utility-header',
    templateUrl: './placement-utility-header.component.html',
    styleUrls: ['./placement-utility-header.component.css'],
    standalone: false
})
export class PlacementUtilityHeaderComponent implements OnInit {
  @Input() placementData: PlacementData;
  @Input() placementMaintenance: PlacementMaintenance;

  /** On only for internal staff while programme-overview.orbit-order is on (fails closed). */
  private readonly orbitOrderSwitchOn = signal(false);

  constructor(public themeService: ThemeService, private featureSwitches: FeatureSwitchService) {}

  ngOnInit(): void {
    if (!isInternalStaff()) {
      return;
    }
    this.featureSwitches.isEnabled(FeatureSwitchKeys.programmeOverviewOrbitOrder)
      .pipe(catchError(() => of(false)))
      .subscribe(on => this.orbitOrderSwitchOn.set(on));
  }

  public get learnerNames(): string[] {
    return (this.placementMaintenance?.placementStudents ?? [])
      .map(student => student.studentFirstName + ' ' + student.studentLastName);
  }

  /** The Orbit order code to show, or null when it is not shown: switch off, not internal staff, or no order. */
  public get orbitOrderCode(): string | null {
    const code = this.placementData?.orderCode?.trim();
    return code && this.orbitOrderSwitchOn() ? code : null;
  }

  /** The order in this tier's Orbit TMS, or null when the tier has no Orbit TMS address (the number shows unlinked). */
  public get orbitOrderUrl(): string | null {
    const code = this.orbitOrderCode;
    const base = (environment.orbitTmsUrl ?? '').replace(/\/+$/, '');
    return code && base ? `${base}/orders/edit/${encodeURIComponent(code)}` : null;
  }
}
